import type { SiteRagReport } from '../../types/siteRag';
import { dataCenterFinancialModelV1Schema } from '../../types/dataCenterFinancial';
import { computeDataCenterFinance } from '../dataCenterFinance';
import { extractNumberedRefIds, NUMBERED_REF_TOKEN_RE, maskNumberedRefTokens } from '../citationTokens';
import { customBriefSourceFrame, customBriefSourcesAreCurrent, customBriefReportSourceReceipt } from '../reportGeneration/customBriefSourceCurrency';
import { hashCanonicalJson } from '../reportGeneration/reportContractHash';
import { buildModeledProjectBudgetEvidence, type FrozenModeledProjectBudgetSource } from './modeledProjectBudget';

/** Quotation of one independently admitted saved Financial analysis. This is
 * neither a Brief calculation nor authority for any legacy financial scalar. */
export interface CustomBriefAnnualFinancialMetricsEvidence {
  readonly reportId: string;
  readonly refId: number;
  readonly modelHash: string;
  readonly priceYear: number;
  readonly referenceYear: number;
  readonly npvUsd: number | null;
  readonly irrPct: number | null;
  readonly paybackYears: number | null;
}

export function buildCustomBriefAnnualFinancialMetrics(args: {
  source?: FrozenModeledProjectBudgetSource;
  report?: SiteRagReport;
  /** Host-selected accepted/current siblings, never read from Brief prose. */
  acceptedCurrentReports?: readonly SiteRagReport[];
}): CustomBriefAnnualFinancialMetricsEvidence | undefined {
  const { report, source } = args;
  if (!source || report?.reportType !== 'custom-brief'
    || report.sedId !== source.sed.id
    || (report.siteId !== undefined && report.siteId !== source.siteId)
    || (report._ownerUid !== undefined && report._ownerUid !== source.ownerUid)
    || (report.sedVersionUsed !== undefined && report.sedVersionUsed !== source.sed.version)) return undefined;
  const frame = customBriefSourceFrame(report);
  if (!frame || frame === 'invalid' || !args.acceptedCurrentReports
    || !customBriefSourcesAreCurrent(report, source.siteId, args.acceptedCurrentReports)) return undefined;
  const financials = args.acceptedCurrentReports.filter(row => row.reportType === 'financial');
  if (financials.length !== 1) return undefined;
  const financial = financials[0]!;
  if (financial.qualityHold === 'needs-revision' || financial._metadataOnly
    || financial.siteId !== source.siteId || financial._ownerUid !== source.ownerUid
    || !financial.sedId || !Number.isInteger(financial.sedVersionUsed) || (financial.sedVersionUsed ?? 0) <= 0
    || !financial.financialAssumptionsAtGen
    || financial.financialAnalysis?.modelKind !== 'data-center-capacity-fee'
    || !financial.financialAnalysis.dataCenter) return undefined;
  // The budget helper requires either the exact frozen SED or independently
  // reproduced Financial material dependencies, then recomputes the full
  // current model and validates its original body/quote authority. The saved
  // source receipt and its own inferred citation remain exact below.
  const budget = buildModeledProjectBudgetEvidence({ source, report: financial });
  const parsed = dataCenterFinancialModelV1Schema.safeParse(source.sed.financialAssumptions?.dataCenterModel);
  if (!budget || !parsed.success) return undefined;
  const computed = computeDataCenterFinance(parsed.data), stamp = financial.financialAnalysis;
  if (!computed.computable || hashCanonicalJson(stamp.dataCenter!.result) !== hashCanonicalJson(computed)
    || stamp.headline.npvUsd !== computed.npvUsd || stamp.headline.irrPct !== computed.irrPct
    || stamp.headline.paybackYears !== computed.paybackYears) return undefined;
  const receipt = customBriefReportSourceReceipt(financial);
  const saved = frame.sources.find(row => row.reportId === financial.id);
  if (!saved || hashCanonicalJson(saved) !== hashCanonicalJson(receipt)) return undefined;
  if ((report.references ?? []).filter(ref => ref.field === `custom-brief-report-source:financial:${financial.id}`).length !== 1) return undefined;
  const refs = (report.references ?? []).flatMap((ref, index) => {
    if (ref.field !== `custom-brief-report-source:financial:${financial.id}`
      || ref.source !== `Generated report document — ${financial.customBriefName ?? financial.reportType} (${financial.id}); analytical findings only`
      || ref.url !== `/sites/${encodeURIComponent(source.siteId)}/reports/${encodeURIComponent(financial.id)}`
      || ref.retrievedAt !== financial.createdAt || ref.sourceGrade !== 'inference' || ref.qualification !== 'estimated') return [];
    const items = ref.subItems?.filter(item => item.topic === 'Immutable source revision');
    if (items?.length !== 1) return [];
    try { return hashCanonicalJson(JSON.parse(items[0]!.detail)) === hashCanonicalJson(receipt) ? [index + 1] : []; }
    catch { return []; }
  });
  if (refs.length !== 1 || parsed.data.priceYear == null || parsed.data.referenceYear == null) return undefined;
  return Object.freeze({ reportId: financial.id, refId: refs[0]!, modelHash: budget.modelHash,
    priceYear: parsed.data.priceYear, referenceYear: parsed.data.referenceYear,
    npvUsd: computed.npvUsd, irrPct: computed.irrPct, paybackYears: computed.paybackYears });
}

const SPLIT = /[;\n]|\.(?=\s|$)|\b(?:but|whereas|however)\b/i;
const UPGRADE = /\b(?:guaranteed|verified|certified|contracted|awarded|paid|EPC|equity|debt|after[- ]tax|published|source\s+comparison|alternative\s+(?:model|scenario)|different\s+(?:model|scenario))\b/i;
const NEGATION = /\b(?:not|never|no|if|unless|would|could)\b/i;
const SOURCE_MODEL = /\bFinancial\s+report(?:'s|’s)?\s+annual\s+data[- ]center\s+model\b/i;
function rounded(value: number, expected: number, raw: string) {
  const numeral = /([\d,]+(?:\.(\d+))?)/.exec(raw);
  if (!numeral || !Number.isFinite(value)) return false;
  const stated = Number(numeral[1]!.replace(/,/g, ''));
  const scale = stated === 0 ? 1 : Math.abs(value / stated);
  return Math.abs(value - expected) <= 0.5 * 10 ** -(numeral[2]?.length ?? 0) * scale
    + Number.EPSILON * Math.max(1, Math.abs(expected)) * 8;
}

/** Only the metric's own assertion may cite its exact accepted Financial
 * report. Original input PDFs and the provenance-only frame are insufficient. */
export function checkCustomBriefAnnualFinancialMetric(args: {
  text: string; index: number; raw: string; value: number; unit: string; rangeLow?: number;
  evidence?: CustomBriefAnnualFinancialMetricsEvidence;
}): { supported: boolean; expected: string } | undefined {
  if (!['USD', '%', 'years'].includes(args.unit)) return undefined;
  const lead = args.text.slice(Math.max(0, args.index - 700), args.index).split(SPLIT).pop() ?? '';
  const tail = args.text.slice(args.index + args.raw.length, args.index + args.raw.length + 500).split(SPLIT)[0] ?? '';
  const role = args.unit === 'USD' ? 'NPV' : args.unit === '%' ? 'IRR' : 'payback';
  const roleEnd = new RegExp(`\\b${role}(?:\\s+period)?\\s*(?:(?:is|of|at|equals?)\\s+|[:=]\\s*)?(?:negative\\s+)?$`, 'i');
  if (!roleEnd.test(maskNumberedRefTokens(lead)) || !/\bannual\s+data[- ]center\s+model\b/i.test(lead)
    || (!args.evidence && !SOURCE_MODEL.test(maskNumberedRefTokens(lead)))) return undefined;
  const sentence = lead + args.raw + tail, plain = maskNumberedRefTokens(sentence), evidence = args.evidence;
  const expectedValue = evidence?.[args.unit === 'USD' ? 'npvUsd' : args.unit === '%' ? 'irrPct' : 'paybackYears'];
  const expected = evidence
    ? `Estimated ${role} ${expectedValue ?? 'unavailable'} from accepted Financial report ${evidence.reportId}, annual model ${evidence.modelHash}; cite its own Ref ${evidence.refId}, preserve unlevered pre-tax constant ${evidence.priceYear} USD and reference-year ${evidence.referenceYear} undiscounted construction-inclusive payback.`
    : 'A separately admitted current Financial report, exact immutable Brief source receipt and own inferred report citation, with the same independently recomputed frozen annual model result.';
  const tokens = [...sentence.matchAll(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi'))].map(match => match[0]);
  const ownCitation = evidence && tokens.some(token => extractNumberedRefIds(token).includes(evidence.refId));
  const verifiedCitation = tokens.some(token => /\bverified\s+Ref\b/i.test(token));
  const priceYears = [...plain.matchAll(/\bconstant\s+(\d{4})\s+USD\b/gi)].map(match => Number(match[1]));
  const clockYears = [...plain.matchAll(/\bfrom\s+(?:the\s+)?(\d{4})\s+(?:reference|valuation)\s+year\b/gi)].map(match => Number(match[1]));
  const invalid = !evidence || expectedValue == null || args.rangeLow !== undefined
    || !SOURCE_MODEL.test(plain) || UPGRADE.test(plain) || NEGATION.test(plain)
    || !ownCitation || verifiedCitation || /(?:[<>+×*/-]|\bat\s+(?:least|most))\s*$/i.test(lead)
    || priceYears.some(year => year !== evidence?.priceYear) || clockYears.some(year => year !== evidence?.referenceYear)
    || /^\s*(?:[+×*/<>-]|times\b|multiplied\b)/i.test(tail)
    || (args.unit === 'years'
      ? !/\bestimated\s+undiscounted\s+payback\b/i.test(lead)
        || !new RegExp(`\\bfrom\\s+(?:the\\s+)?${evidence?.referenceYear}\\s+(?:reference|valuation)\\s+year\\b`, 'i').test(tail)
        || !/\bincluding\s+construction\b/i.test(tail)
        || /\b(?:COD|commissioning|excluding\s+construction|discounted\s+payback)\b/i.test(plain)
      : !new RegExp(`\\bestimated\\s+${role}\\b`, 'i').test(lead)
        || !/\bunlevered\s+pre[- ]tax\s+basis\b/i.test(tail)
        || (args.unit === 'USD' && !new RegExp(`\\bconstant\\s+${evidence?.priceYear}\\s+USD\\b`, 'i').test(tail)));
  const negative = /\bnegative\s*$/i.test(lead);
  return { supported: !invalid && rounded(negative ? -args.value : args.value, expectedValue!, args.raw), expected };
}
